import * as React from 'react';
import * as ldJs from 'launchdarkly-js-client-sdk';
import type { LDInfo } from '~/utils/contexts';
import { LaunchDarklyContext } from '~/utils/contexts';

type LDBrowserProps = { children: React.ReactNode };

class LDBrowser extends React.Component<LDBrowserProps, LDInfo> {
  readonly state: Readonly<LDInfo>;

  constructor(props: LDBrowserProps) {
    super(props);
    const { ssrFlags, ldContext } = window.__ld;
    const clientSideID = window.ENV.LD_CLIENT_SIDE_ID;

    console.log(`initializing ld client with ${clientSideID}...`);
    const ldClient = ldJs.initialize(clientSideID, ldContext, { bootstrap: ssrFlags });

    ldClient.on('change', (changes: ldJs.LDFlagChangeset) => {
      const flattened = getFlattenedFlagsFromChangeset(changes, ssrFlags);
      if (Object.keys(flattened).length > 0) {
        this.setState(({ flags }) => ({ flags: { ...flags, ...flattened } }));
      }
    });

    this.state = {
      flags: ssrFlags,
      ldClient,
      ldContext,
    };
  }

  render() {
    return <LaunchDarklyContext.Provider value={this.state}>{this.props.children}</LaunchDarklyContext.Provider>;
  }
}

/**
 * Gets the flags to pass to the provider from the changeset.
 *
 * @param changes the `LDFlagChangeset` from the ldClient onchange handler.
 * @param targetFlags if targetFlags are specified, changes to other flags are ignored and not returned in
 * the flattened `LDFlagSet`
 * @return an `LDFlagSet` with the current flag values from the LDFlagChangeset filtered by `targetFlags`.
 * The returned object may be empty `{}` if none of the targetFlags were changed.
 */
export const getFlattenedFlagsFromChangeset = (
  changes: ldJs.LDFlagChangeset,
  targetFlags: ldJs.LDFlagSet | undefined
) => {
  const flattened: ldJs.LDFlagSet = {};

  for (const key in changes) {
    if (!targetFlags || targetFlags[key] !== undefined) {
      flattened[key] = changes[key].current;
    }
  }

  return flattened;
};

export default LDBrowser;
